import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Header, Footer } from '@/components/layout'
import { SEO } from '@/components/seo'

type FormData = {
  // Firm details
  firmName: string
  firmWebsite: string
  province: string
  firmSize: string
  yearsInBusiness: string
  // Contact person
  contactName: string
  contactTitle: string
  contactEmail: string
  contactPhone: string
  // Practice details
  services: string[]
  clientCount: string
  clientTypes: string[]
  // Program details
  howPromote: string
  referralSource: string
  additionalNotes: string
  // Agreements
  agreeTerms: boolean
  agreeComms: boolean
}

const initialForm: FormData = {
  firmName: '',
  firmWebsite: '',
  province: '',
  firmSize: '',
  yearsInBusiness: '',
  contactName: '',
  contactTitle: '',
  contactEmail: '',
  contactPhone: '',
  services: [],
  clientCount: '',
  clientTypes: [],
  howPromote: '',
  referralSource: '',
  additionalNotes: '',
  agreeTerms: false,
  agreeComms: false,
}

const provinces = [
  'Central', 'Eastern', 'North Central', 'North Western',
  'Northern', 'Sabaragamuwa', 'Southern', 'Uva', 'Western',
]

const firmSizes = [
  'Solo practitioner (1 person)',
  'Small firm (2–10 staff)',
  'Mid-size firm (11–50 staff)',
  'Large firm (50+ staff)',
]

const serviceOptions = [
  'Bookkeeping',
  'Tax preparation',
  'Payroll processing',
  'Financial statement preparation',
  'Audit & assurance',
  'Business advisory',
  'Estate planning',
  'CFO services',
]

const clientTypeOptions = [
  'Freelancers & sole proprietors',
  'Small businesses (1–10 employees)',
  'Medium businesses (11–50 employees)',
  'Large businesses (50+ employees)',
  'Medical / dental practices',
  'Law offices',
  'Real estate professionals',
  'Non-profit organizations',
  'Contractors & trades',
]

const referralSources = [
  'Google search',
  'Social media',
  'Referred by a colleague',
  'Industry event or conference',
  'Email newsletter',
  'Other',
]

type Step = 1 | 2 | 3 | 4

const stepLabels: Record<Step, string> = {
  1: 'Firm details',
  2: 'Contact person',
  3: 'Practice & clients',
  4: 'Partnership details',
}

function CheckboxGroup({
  options,
  selected,
  onChange,
}: {
  options: string[]
  selected: string[]
  onChange: (val: string[]) => void
}) {
  const toggle = (option: string) => {
    onChange(
      selected.includes(option)
        ? selected.filter((s) => s !== option)
        : [...selected, option]
    )
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((option) => (
        <label
          key={option}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${
            selected.includes(option)
              ? 'border-brand-500 bg-brand-50 text-brand-700'
              : 'border-surface-200 text-surface-700 hover:border-surface-300'
          }`}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={selected.includes(option)}
            onChange={() => toggle(option)}
          />
          <span
            className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${
              selected.includes(option)
                ? 'border-brand-600 bg-brand-600'
                : 'border-surface-300'
            }`}
          >
            {selected.includes(option) && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            )}
          </span>
          {option}
        </label>
      ))}
    </div>
  )
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-red-500">{message}</p>
}

export default function PartnerApplicationPage() {
  const [step, setStep] = useState<Step>(1)
  const [form, setForm] = useState<FormData>(initialForm)
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({})
  const [submitted, setSubmitted] = useState(false)

  const set = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    setErrors((e) => ({ ...e, [key]: undefined }))
  }

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-3 rounded-xl border text-sm text-surface-900 placeholder-surface-400 outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100 ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-surface-200 bg-white hover:border-surface-300'
    }`

  const validateStep = (): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {}

    if (step === 1) {
      if (!form.firmName.trim()) e.firmName = 'Firm name is required.'
      if (!form.province) e.province = 'Please select a province.'
      if (!form.firmSize) e.firmSize = 'Please select a firm size.'
      if (!form.yearsInBusiness) e.yearsInBusiness = 'Please select years in business.'
    }

    if (step === 2) {
      if (!form.contactName.trim()) e.contactName = 'Full name is required.'
      if (!form.contactTitle.trim()) e.contactTitle = 'Job title is required.'
      if (!form.contactEmail.trim()) e.contactEmail = 'Email is required.'
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail))
        e.contactEmail = 'Please enter a valid email address.'
      if (!form.contactPhone.trim()) e.contactPhone = 'Phone number is required.'
    }

    if (step === 3) {
      if (form.services.length === 0) e.services = 'Please select at least one service.'
      if (!form.clientCount) e.clientCount = 'Please select an approximate client count.'
      if (form.clientTypes.length === 0) e.clientTypes = 'Please select at least one client type.'
    }

    if (step === 4) {
      if (!form.howPromote.trim()) e.howPromote = 'Please describe how you plan to promote Ledger.'
      if (!form.referralSource) e.referralSource = 'Please tell us how you heard about us.'
      if (!form.agreeTerms) e.agreeTerms = 'You must agree to the partner terms to continue.'
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleNext = () => {
    if (validateStep()) setStep((s) => (s < 4 ? ((s + 1) as Step) : s))
  }

  const handleBack = () => setStep((s) => (s > 1 ? ((s - 1) as Step) : s))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateStep()) return
    // In production, POST form data to your API here
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Application Submitted" description="Your partner application has been submitted." canonical="/partner-application" />
        <Header />
        <main className="pt-32 pb-20">
          <div className="mx-auto max-w-lg section-padding text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-50 mb-6">
              <svg className="w-10 h-10 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-surface-900 mb-3">Application received!</h1>
            <p className="text-surface-600 mb-2">
              Thank you, <strong>{form.contactName}</strong>. We've received your application for <strong>{form.firmName}</strong>.
            </p>
            <p className="text-surface-600 mb-8">
              Our team reviews applications within <strong>24–48 hours</strong>. We'll be in touch at <strong>{form.contactEmail}</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/referral" className="btn btn-primary px-6 py-3">
                Back to referral program
              </Link>
              <Link to="/" className="btn btn-secondary px-6 py-3">
                Go to homepage
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Partner Application"
        description="Apply to become a Ledger referral partner. Accounting firms earn 30% recurring commission for every client they refer."
        canonical="/partner-application"
      />
      <Header />

      <main className="pt-28 pb-20">
        <div className="mx-auto max-w-2xl section-padding">

          {/* Header */}
          <div className="mb-10">
            <Link to="/referral" className="inline-flex items-center gap-1.5 text-sm text-surface-500 hover:text-surface-700 mb-6 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Back to referral program
            </Link>
            <h1 className="text-3xl font-bold text-surface-900">Partner application</h1>
            <p className="mt-2 text-surface-600">
              Complete the form below to apply. We review all applications within 24–48 hours.
            </p>
          </div>

          {/* Stepper */}
          <div className="flex items-center gap-2 mb-10">
            {([1, 2, 3, 4] as Step[]).map((s) => (
              <div key={s} className="flex items-center gap-2 flex-1 last:flex-none">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                      s < step
                        ? 'bg-brand-600 text-white'
                        : s === step
                        ? 'bg-brand-600 text-white ring-4 ring-brand-100'
                        : 'bg-surface-100 text-surface-400'
                    }`}
                  >
                    {s < step ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      s
                    )}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${s === step ? 'text-surface-900' : 'text-surface-400'}`}>
                    {stepLabels[s]}
                  </span>
                </div>
                {s < 4 && <div className={`h-px flex-1 mx-1 ${s < step ? 'bg-brand-400' : 'bg-surface-200'}`} />}
              </div>
            ))}
          </div>

          {/* Form card */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="card p-8">

              {/* Step 1 — Firm details */}
              {step === 1 && (
                <div className="space-y-5">
                  <h2 className="text-lg font-semibold text-surface-900">About your firm</h2>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1.5">Firm / practice name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Smith & Associates CPA"
                      value={form.firmName}
                      onChange={(e) => set('firmName', e.target.value)}
                      className={inputClass('firmName')}
                    />
                    <FieldError message={errors.firmName} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1.5">Website <span className="text-surface-400 font-normal">(optional)</span></label>
                    <input
                      type="url"
                      placeholder="https://yourfirm.ca"
                      value={form.firmWebsite}
                      onChange={(e) => set('firmWebsite', e.target.value)}
                      className={inputClass('firmWebsite')}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-surface-700 mb-1.5">Province <span className="text-red-500">*</span></label>
                      <select
                        value={form.province}
                        onChange={(e) => set('province', e.target.value)}
                        className={inputClass('province')}
                      >
                        <option value="">Select province</option>
                        {provinces.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <FieldError message={errors.province} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-surface-700 mb-1.5">Years in business <span className="text-red-500">*</span></label>
                      <select
                        value={form.yearsInBusiness}
                        onChange={(e) => set('yearsInBusiness', e.target.value)}
                        className={inputClass('yearsInBusiness')}
                      >
                        <option value="">Select range</option>
                        <option>Less than 1 year</option>
                        <option>1–3 years</option>
                        <option>3–5 years</option>
                        <option>5–10 years</option>
                        <option>10+ years</option>
                      </select>
                      <FieldError message={errors.yearsInBusiness} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-2">Firm size <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {firmSizes.map((size) => (
                        <label
                          key={size}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${
                            form.firmSize === size
                              ? 'border-brand-500 bg-brand-50 text-brand-700'
                              : 'border-surface-200 text-surface-700 hover:border-surface-300'
                          }`}
                        >
                          <input
                            type="radio"
                            className="sr-only"
                            checked={form.firmSize === size}
                            onChange={() => set('firmSize', size)}
                          />
                          <span
                            className={`w-4 h-4 rounded-full flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                              form.firmSize === size ? 'border-brand-600' : 'border-surface-300'
                            }`}
                          >
                            {form.firmSize === size && <span className="w-2 h-2 rounded-full bg-brand-600" />}
                          </span>
                          {size}
                        </label>
                      ))}
                    </div>
                    <FieldError message={errors.firmSize} />
                  </div>
                </div>
              )}

              {/* Step 2 — Contact person */}
              {step === 2 && (
                <div className="space-y-5">
                  <h2 className="text-lg font-semibold text-surface-900">Primary contact</h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-surface-700 mb-1.5">Full name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        placeholder="Jane Smith"
                        value={form.contactName}
                        onChange={(e) => set('contactName', e.target.value)}
                        className={inputClass('contactName')}
                      />
                      <FieldError message={errors.contactName} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-surface-700 mb-1.5">Job title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. Managing Partner, CPA"
                        value={form.contactTitle}
                        onChange={(e) => set('contactTitle', e.target.value)}
                        className={inputClass('contactTitle')}
                      />
                      <FieldError message={errors.contactTitle} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1.5">Business email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      placeholder="jane@yourfirm.ca"
                      value={form.contactEmail}
                      onChange={(e) => set('contactEmail', e.target.value)}
                      className={inputClass('contactEmail')}
                    />
                    <FieldError message={errors.contactEmail} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1.5">Phone number <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      placeholder="+1 (416) 000-0000"
                      value={form.contactPhone}
                      onChange={(e) => set('contactPhone', e.target.value)}
                      className={inputClass('contactPhone')}
                    />
                    <FieldError message={errors.contactPhone} />
                  </div>

                  <div className="rounded-xl bg-brand-50 border border-brand-100 px-5 py-4 flex gap-3">
                    <svg className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                    <p className="text-sm text-brand-700">
                      Your contact details are kept confidential and used only to review and manage your partner application.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3 — Practice & clients */}
              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-surface-900">Your practice & clients</h2>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-2">
                      Services your firm offers <span className="text-red-500">*</span>
                    </label>
                    <CheckboxGroup
                      options={serviceOptions}
                      selected={form.services}
                      onChange={(v) => set('services', v)}
                    />
                    <FieldError message={errors.services} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1.5">
                      Approximate number of active clients <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.clientCount}
                      onChange={(e) => set('clientCount', e.target.value)}
                      className={inputClass('clientCount')}
                    >
                      <option value="">Select range</option>
                      <option>1–10 clients</option>
                      <option>11–25 clients</option>
                      <option>26–50 clients</option>
                      <option>51–100 clients</option>
                      <option>100+ clients</option>
                    </select>
                    <FieldError message={errors.clientCount} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-2">
                      Types of clients you serve <span className="text-red-500">*</span>
                    </label>
                    <CheckboxGroup
                      options={clientTypeOptions}
                      selected={form.clientTypes}
                      onChange={(v) => set('clientTypes', v)}
                    />
                    <FieldError message={errors.clientTypes} />
                  </div>
                </div>
              )}

              {/* Step 4 — Partnership details */}
              {step === 4 && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-surface-900">About the partnership</h2>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1.5">
                      How do you plan to promote Ledger to your clients? <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      placeholder="e.g. During annual tax reviews, I discuss software options with clients who need better bookkeeping. I'd share your platform as a recommendation alongside our filing services."
                      value={form.howPromote}
                      onChange={(e) => set('howPromote', e.target.value)}
                      className={`${inputClass('howPromote')} resize-none`}
                    />
                    <FieldError message={errors.howPromote} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1.5">
                      How did you hear about us? <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.referralSource}
                      onChange={(e) => set('referralSource', e.target.value)}
                      className={inputClass('referralSource')}
                    >
                      <option value="">Select an option</option>
                      {referralSources.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                    <FieldError message={errors.referralSource} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1.5">
                      Anything else you'd like us to know? <span className="text-surface-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Any questions, special considerations, or context about your firm..."
                      value={form.additionalNotes}
                      onChange={(e) => set('additionalNotes', e.target.value)}
                      className={`${inputClass('additionalNotes')} resize-none`}
                    />
                  </div>

                  {/* Agreements */}
                  <div className="space-y-3 pt-2">
                    <label className={`flex items-start gap-3 cursor-pointer group`}>
                      <span
                        onClick={() => set('agreeTerms', !form.agreeTerms)}
                        className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${
                          form.agreeTerms ? 'border-brand-600 bg-brand-600' : 'border-surface-300 group-hover:border-surface-400'
                        }`}
                      >
                        {form.agreeTerms && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        )}
                      </span>
                      <span className="text-sm text-surface-600">
                        I agree to the{' '}
                        <Link to="/terms" className="text-brand-600 hover:underline" target="_blank">Partner Terms & Conditions</Link>
                        {' '}and confirm that the information provided is accurate. <span className="text-red-500">*</span>
                      </span>
                    </label>
                    <FieldError message={errors.agreeTerms} />

                    <label className="flex items-start gap-3 cursor-pointer group">
                      <span
                        onClick={() => set('agreeComms', !form.agreeComms)}
                        className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${
                          form.agreeComms ? 'border-brand-600 bg-brand-600' : 'border-surface-300 group-hover:border-surface-400'
                        }`}
                      >
                        {form.agreeComms && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        )}
                      </span>
                      <span className="text-sm text-surface-600">
                        I'd like to receive partner updates, commission reports, and promotional materials from Ledger. <span className="text-surface-400">(optional)</span>
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-surface-100">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="btn btn-secondary px-6 py-2.5 text-sm"
                  >
                    Back
                  </button>
                ) : (
                  <div />
                )}

                {step < 4 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="btn btn-primary px-6 py-2.5 text-sm"
                  >
                    Continue
                    <svg className="w-4 h-4 ml-1 inline" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-primary px-8 py-2.5 text-sm"
                  >
                    Submit application
                  </button>
                )}
              </div>
            </div>
          </form>

          <p className="text-center text-xs text-surface-400 mt-6">
            Questions? Email us at{' '}
            <a href="mailto:partners@ledger.lk" className="text-brand-600 hover:underline">
              partners@ledger.lk
            </a>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  )
}
